import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  ChevronLeft,
  ArrowRight,
  Minus,
  Plus,
  Check,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useStoreStore, CURRENCY_MAP, periodNoteFor, totalPeriodFor, featureLineFor } from '@/stores/store-store';
import type { Product, Price } from '@/stores/store-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';

const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD', symbol: '$' },
  { value: 'eur', label: 'EUR', symbol: '\u20AC' },
  { value: 'inr', label: 'INR', symbol: '\u20B9' },
  { value: 'gbp', label: 'GBP', symbol: '\u00A3' },
  { value: 'aud', label: 'AUD', symbol: 'A$' },
];

/**
 * Inline plans browser for the subscription settings page.
 * Loads products from Stripe via store-store, filters to the
 * current app, and lets the user pick a plan + proceed to payment.
 */
export function PlansBrowser() {
  const products = useStoreStore((s) => s.products);
  const prices = useStoreStore((s) => s.prices);
  const loadingProducts = useStoreStore((s) => s.loadingProducts);
  const loadProducts = useStoreStore((s) => s.loadProducts);
  const selectedCurrency = useStoreStore((s) => s.selectedCurrency);
  const setCurrency = useStoreStore((s) => s.setCurrency);
  const processingPayment = useStoreStore((s) => s.processingPayment);
  const proceedToPayment = useStoreStore((s) => s.proceedToPayment);
  const selectProduct = useStoreStore((s) => s.selectProduct);
  const selectPaymentPlan = useStoreStore((s) => s.selectPaymentPlan);
  const setQuantity = useStoreStore((s) => s.setQuantity);
  const quantity = useStoreStore((s) => s.quantity);

  const user = useAuthStore((s) => s.user);
  const extensionName = useAppStore((s) => s.extensionName);

  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');

  // Load products on mount if not already loaded — only fetch prices for current app
  useEffect(() => {
    if (products.length === 0 && !loadingProducts && extensionName) {
      loadProducts(extensionName);
    }
  }, [products.length, loadingProducts, loadProducts, extensionName]);

  // Find the product matching the current app
  const appProduct = useMemo<Product | null>(() => {
    if (!extensionName || products.length === 0) return null;
    const ext = extensionName.toLowerCase();
    return (
      products.find(
        (p) =>
          p.stripe_metadata_appcode &&
          p.stripe_metadata_appcode.toLowerCase() === ext,
      ) ?? null
    );
  }, [products, extensionName]);

  // All active recurring prices for the matched product + selected currency
  const allProductPrices = useMemo<Price[]>(() => {
    if (!appProduct) return [];
    const all = prices[appProduct.id] || [];
    return all.filter(
      (p) => p.currency === selectedCurrency && p.active && p.type !== 'one_time',
    );
  }, [appProduct, prices, selectedCurrency]);

  // Check if both monthly and yearly prices exist
  const hasMonthly = allProductPrices.some((p) => p.interval === 'month');
  const hasYearly = allProductPrices.some((p) => p.interval === 'year');
  const showIntervalToggle = hasMonthly && hasYearly;

  // Filter by selected billing interval (or show all if only one interval exists)
  const productPrices = useMemo<Price[]>(() => {
    if (!showIntervalToggle) return allProductPrices;
    return allProductPrices.filter((p) => p.interval === billingInterval);
  }, [allProductPrices, billingInterval, showIntervalToggle]);

  // Build a map of yearly discount percentages (compared to monthly equivalent)
  const yearlyDiscounts = useMemo<Record<string, number>>(() => {
    const discounts: Record<string, number> = {};
    const monthlyPrices = productPrices.filter((p) => p.interval === 'month');
    const yearlyPrices = productPrices.filter((p) => p.interval === 'year');

    for (const yearly of yearlyPrices) {
      // Find the cheapest monthly price to compare against
      if (monthlyPrices.length > 0) {
        // Use the lowest monthly price as the baseline
        const monthlyBaseline = monthlyPrices.reduce((min, p) =>
          p.unit_amount < min.unit_amount ? p : min,
        );
        const monthlyEquivalent = monthlyBaseline.unit_amount * 12;
        if (monthlyEquivalent > 0 && yearly.unit_amount < monthlyEquivalent) {
          const pct = Math.round((1 - yearly.unit_amount / monthlyEquivalent) * 100);
          if (pct > 0) discounts[yearly.id] = pct;
        }
      }
    }
    return discounts;
  }, [productPrices]);

  const selectedPriceObj = useMemo(
    () => productPrices.find((p) => p.id === selectedPriceId),
    [productPrices, selectedPriceId],
  );

  // Sync store-store's selected product/plan state
  const handleSelectPlan = (priceId: string) => {
    if (!appProduct) return;
    selectProduct(appProduct.id);
    selectPaymentPlan(priceId);
    setSelectedPriceId(priceId);
  };

  const handleBack = () => {
    selectPaymentPlan(null);
    setSelectedPriceId(null);
  };

  const paymentPending = useStoreStore((s) => s.paymentPending);
  const lastCheckoutUrl = useStoreStore((s) => s.lastCheckoutUrl);
  const loadSubscriptions = useStoreStore((s) => s.loadSubscriptions);

  const handleProceed = () => {
    if (user?.uid) {
      proceedToPayment(user.uid, { openInNewTab: true });
    }
  };

  const handleRefreshStatus = async () => {
    if (user?.uid) {
      useStoreStore.setState({ paymentPending: false });
      await loadSubscriptions(user.uid);
    }
  };

  // ── Loading state ──
  if (loadingProducts) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading plans...</span>
      </div>
    );
  }

  // ── No product found for this app ──
  if (!appProduct) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No plans available for this app.
      </p>
    );
  }

  // ── Quantity picker (after selecting a plan) ──
  if (selectedPriceId && selectedPriceObj) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to plans
        </button>

        <div className="text-center">
          <Badge variant="secondary" className="text-sm px-4 py-1">
            {selectedPriceObj.description || selectedPriceObj.nickname || 'Selected Plan'}
          </Badge>
        </div>

        <div className="text-center space-y-1">
          <h3 className="font-display text-base font-semibold text-foreground">How many users?</h3>
        </div>

        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
            className="h-10 w-10"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val > 0) setQuantity(val);
            }}
            className="w-20 text-center text-lg font-bold h-10"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity(quantity + 1)}
            className="h-10 w-10"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="rounded-xl bg-muted/50 border border-border px-5 py-3 text-center">
          <p className="text-xs text-muted-foreground mb-0.5">Total</p>
          <p className="font-display text-xl font-bold tabular-nums text-foreground">
            {CURRENCY_MAP[selectedPriceObj.currency] || '$'}
            {((selectedPriceObj.unit_amount * quantity) / 100).toFixed(2)}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              / {totalPeriodFor(appProduct, selectedPriceObj)}
            </span>
          </p>
        </div>

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleProceed}
            disabled={processingPayment}
            className="px-10"
          >
            {processingPayment && <Loader2 className="h-4 w-4 animate-spin" />}
            Proceed to Payment
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-center text-[11px] text-muted-foreground/60 mt-3">
          By proceeding, you agree to our{' '}
          <a
            href="https://apps.ulgebra.com/cancel-refund-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-muted-foreground"
          >
            Cancellation & Refund Policy
          </a>
        </p>

        {/* Payment overlay — full page, covers everything */}
        {(processingPayment || paymentPending) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-6 text-center px-8 animate-in fade-in duration-300">
              {processingPayment ? (
                <>
                  <div className="relative">
                    <div className="h-16 w-16 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CreditCard className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-foreground">Setting up your checkout</p>
                    <p className="text-sm text-muted-foreground mt-1">Opening Stripe in a new tab...</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30">
                    <CreditCard className="h-7 w-7 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-foreground">Checkout is open in another tab</p>
                    <p className="text-sm text-muted-foreground mt-1">Complete your payment there, then come back here.</p>
                  </div>
                  {lastCheckoutUrl && (
                    <button
                      onClick={() => window.open(lastCheckoutUrl, '_blank')}
                      className="text-xs text-primary hover:underline"
                    >
                      Popup not opened? Click here to open checkout
                    </button>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <Button
                      onClick={handleRefreshStatus}
                      className="px-6"
                    >
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      I have paid — Refresh
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => useStoreStore.setState({ paymentPending: false, lastCheckoutUrl: null })}
                      className="text-muted-foreground"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Plan cards ──
  return (
    <div className="space-y-5">
      {/* Billing interval toggle + Currency selector — same row */}
      <div className="flex items-center gap-2">
        {showIntervalToggle && (
          <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-1 border border-border h-9">
            <button
              onClick={() => setBillingInterval('month')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 h-7',
                billingInterval === 'month'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background',
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('year')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 h-7',
                billingInterval === 'year'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background',
              )}
            >
              Yearly
              {hasYearly && hasMonthly && (
                <span className="ml-1 text-[10px] text-emerald-400 font-semibold">Save</span>
              )}
            </button>
          </div>
        )}
        <select
          value={selectedCurrency}
          onChange={(e) => setCurrency(e.target.value)}
          className="h-9 rounded-lg border border-border bg-muted/50 px-3 text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
        >
          {CURRENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.symbol} {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Plans grid */}
      {productPrices.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          No pricing plans available for this currency.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {productPrices.map((price, idx) => {
            const symbol = CURRENCY_MAP[price.currency] || '$';
            const amount = (price.unit_amount / 100).toFixed(2);
            const periodNote = periodNoteFor(appProduct, price);
            const perDisplay = periodNote
              ? `${periodNote} / ${price.interval}`
              : price.interval;
            const isPopular = idx === 1 && productPrices.length > 1;
            const discountPct = yearlyDiscounts[price.id];

            // Extract features from product metadata
            const features: string[] = [];
            Object.keys(appProduct).forEach((key) => {
              if (key.startsWith('stripe_metadata_plan_feat_') && price.description) {
                const stripeFeatPlanName = key.split('stripe_metadata_plan_feat_')[1];
                if (
                  price.description.toLowerCase().replaceAll(/[\W_]+/g, '') ===
                  stripeFeatPlanName
                ) {
                  (appProduct[key] as string).split('\n').forEach((line: string) => {
                    if (line.trim()) features.push(featureLineFor(appProduct, line));
                  });
                }
              }
            });

            return (
              <Card
                key={price.id}
                className={cn(
                  'relative cursor-pointer transition-all duration-200',
                  isPopular
                    ? 'border-primary border-2 shadow-[var(--shadow-card-hover)]'
                    : 'hover:border-primary/30 hover:shadow-[var(--shadow-card-hover)]',
                )}
                onClick={() => handleSelectPlan(price.id)}
              >
                {/* Gradient top border for popular plan */}
                {isPopular && (
                  <div className="absolute top-0 left-4 right-4 h-[2px] rounded-full bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                )}
                {isPopular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <Badge variant="default" className="px-2.5 py-px text-[10px] shadow-sm">
                      Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-5 flex flex-col h-full">
                  <h4 className="font-display text-sm font-semibold text-foreground text-center">
                    {price.description || price.nickname || 'Plan'}
                  </h4>
                  <div className="text-center mt-3 mb-0.5">
                    <span className="font-display text-2xl font-bold tabular-nums text-foreground">
                      {symbol}{amount}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mb-1">
                    / {perDisplay}
                  </p>
                  {discountPct && (
                    <p className="text-center mb-3">
                      <span className="inline-block text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 rounded-full px-2 py-0.5 animate-fade-in">
                        Save {discountPct}%
                      </span>
                    </p>
                  )}
                  {!discountPct && <div className="mb-3" />}

                  {features.length > 0 && (
                    <ul className="space-y-2 mb-4 flex-1">
                      {features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    variant={isPopular ? 'default' : 'outline'}
                    size="sm"
                    className="w-full mt-auto"
                  >
                    {price.trial_period_days && price.trial_period_days > 0
                      ? `Start ${price.trial_period_days}-Day Trial`
                      : 'Select Plan'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
